import type { FaqItem } from "@/components/sections/products/shared/faq/types";
import { DOCS } from "@/lib/constants";

export const MOBILE_PRODUCT_FAQS: FaqItem[] = [
  {
    q: "What is a mobile proxy?",
    a: "A mobile proxy routes internet traffic through an IP address assigned to a 4G or 5G mobile connection. It is useful when a workflow needs a mobile network identity, such as mobile ad verification, localized mobile SERP checks, or testing how a public page appears to mobile users. Proxidize manages targeting, credentials, session behavior, usage, and IP rotation in the cloud, so you do not need to operate phones, modems, or proxy hardware.",
  },
  {
    q: "Does unused mobile bandwidth roll over?",
    a: "No. Included Mobile Per GB bandwidth expires after one month. If non-expiring bandwidth is important to your workflow, compare Proxidize Residential Proxies.",
  },
  {
    q: "What's the difference between Per GB and Per Proxy plans?",
    a: "Per GB gives you access to a large proxy pool and you pay only for the bandwidth you use, with no cap on the number of access points. Per Proxy gives you real mobile IPs, each with its own credentials, location, ISP preference, rotation settings, and usage tracking, starting at $59 per proxy per month. Per GB is best for web scraping and data collection. Per Proxy is best for workflows that need a consistent identity.",
    link: { label: "Compare both plans in the docs", href: DOCS.perProxyPlan },
  },
  {
    q: "Is signing up free?",
    a: "Creating a Proxidize account is free. Paid Mobile Per GB plans start at $50 for 25 GB, and Per Proxy plans start at $59 per proxy per month.",
  },
  {
    q: "How does Per GB pricing work?",
    a: "Self-serve mobile plans are monthly bandwidth packages priced at $2/GB, starting at $50 for 25 GB. Included mobile bandwidth expires after one month. You get access to a large proxy pool with no cap on the number of access points, and custom volume pricing is available above 10 TB.",
    link: { label: "Read the Per GB docs", href: DOCS.mobilePerGbPlan },
  },
  {
    q: "Can I target specific cities?",
    a: "Yes. Both Per GB and Per Proxy plans support city-level targeting and ASN/ISP targeting. Select your city from the dashboard when configuring your proxy.",
  },
  {
    q: "Is KYC required?",
    a: "Yes. We require KYC (Know Your Customer) verification from all customers to maintain compliance and prevent misuse of our network.",
  },
  {
    q: "How fast are Proxidize mobile proxies?",
    a: "Typical speeds range from 10–50 Mbps, with burst speeds reaching up to 100 Mbps. Performance varies by location, network, and target.",
  },
  {
    q: "How does IP rotation work?",
    a: "On Per Proxy, Proxidize uses a proprietary rotation mechanism averaging less than 500ms per rotation. On Per GB, you can rotate instantly on every request or hold a sticky session for up to 3 days, depending on your workflow.",
    link: { label: "Rotation endpoints in the API reference", href: DOCS.apiPerProxyMobile },
  },
  {
    q: "What happens if my proxy endpoint goes down?",
    a: "Our automated failover system monitors all mobile endpoints continuously. If your endpoint fails, automated failover routes requests to another available endpoint with no changes to your proxy credentials.",
    link: { label: "Troubleshooting guide", href: DOCS.troubleshooting },
  },
  {
    q: "What protocols are supported?",
    a: "HTTP, HTTPS, and SOCKS5 across all plans.",
  },
  {
    q: "Can I use mobile proxies with AI agents?",
    a: "Yes. Connect the agent's actual traffic through HTTP, HTTPS, or SOCKS5 — a Playwright example using generated mobile credentials is in our developer section. The REST API and the open-source Proxidize MCP server are separate interfaces for managing your access points (rotating cities, generating credentials) rather than routing traffic. Reach for mobile IPs when the task specifically needs a genuine mobile network identity, such as checking a localized landing page or a mobile ad as it renders for mobile users.",
    link: {
      label: "Mobile proxies for developers",
      href: "/proxy-server/mobile-proxies#mobile-for-developers-heading",
    },
  },
  {
    q: "Should my agent use residential or mobile proxies?",
    a: "Residential is generally the starting choice for broad data collection — lower cost per GB, coverage across 195+ countries, and IPs that blend into everyday traffic. Choose mobile proxies when the workflow specifically requires a mobile network identity, such as verifying how content, pricing, or ads appear to real mobile-network users in a given city.",
    link: {
      label: "Compare mobile vs. residential",
      href: "/proxy-server/mobile-proxies#compare-proxies",
    },
  },
  {
    q: "How do I choose a rotating mobile proxy provider?",
    a: "Compare network coverage, rotation control, city-level targeting, success rates, and pricing model across providers before committing.",
    link: {
      label: "Best rotating mobile proxy providers",
      href: "/blog/best-rotating-mobile-proxy-providers/",
    },
  },
  {
    q: "Is there a money-back guarantee?",
    a: "Yes. All plans come with a 30-day money-back guarantee, when less than 10 GB has been used on the plan or proxy.",
  },
  {
    q: "What does unlimited bandwidth mean on the Per Proxy plan?",
    a: "Each Per Proxy plan includes unlimited bandwidth, so you will never be cut off for high data usage. Each proxy comes with 50GB of prioritized data per month. If you exceed that, speeds may be deprioritized depending on overall network load, but your connection stays active regardless of how much data you use.",
  },
];
